import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import * as yup from 'yup';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import MuiTextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Badge from '@mui/material/Badge';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DashboardIcon from '@mui/icons-material/Dashboard';
import GroupIcon from '@mui/icons-material/Group';
import SecurityIcon from '@mui/icons-material/Security';

import defaultClient from '@src/api/clients/default';
import { FormBase, TextField } from '@src/components/form';
import { useAuth } from '@src/context';
import { useMessage } from '@src/context/common';
import useForm from '@src/hooks/useForm';
import paths from '@src/paths';

const profileSchema = yup.object().shape({
  first_name: yup.string().max(150).label('First name'),
  last_name: yup.string().max(150).label('Last name'),
});

const emailSchema = yup.object().shape({
  email: yup.string().email('Enter a valid email').required('Email is required'),
});

const passwordSchema = yup.object().shape({
  old_password: yup.string().required('Current password is required'),
  new_password1: yup.string().min(8, 'Use at least 8 characters').required('New password is required'),
  new_password2: yup
    .string()
    .oneOf([yup.ref('new_password1'), null], 'Passwords must match')
    .required('Please confirm your new password'),
});

const ROLE_LABELS = {
  viewer: 'Viewer',
  editor: 'Editor',
  admin: 'Admin',
};

const ROLE_COLORS = {
  viewer: 'default',
  editor: 'info',
  admin: 'warning',
};

function getInitials(firstName, lastName, email) {
  if (firstName || lastName) {
    return [firstName?.[0], lastName?.[0]].filter(Boolean).join('').toUpperCase();
  }
  return (email?.[0] || '?').toUpperCase();
}

function formatDate(dateStr) {
  if (!dateStr) return 'Never';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
}

function formatShortDate(dateStr) {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

function getPasswordStrength(password) {
  if (!password) return { score: 0, label: '', color: 'inherit' };
  let score = 0;
  if (password.length >= 8) score += 1;
  if (password.length >= 12) score += 1;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score += 1;
  if (/\d/.test(password)) score += 1;
  if (/[^a-zA-Z0-9]/.test(password)) score += 1;

  if (score <= 1) return { score: 20, label: 'Weak', color: 'error' };
  if (score === 2) return { score: 40, label: 'Fair', color: 'warning' };
  if (score === 3) return { score: 60, label: 'Good', color: 'info' };
  if (score === 4) return { score: 80, label: 'Strong', color: 'success' };
  return { score: 100, label: 'Very strong', color: 'success' };
}

function SaveButton({ children, saved, ...props }) {
  if (saved) {
    return (
      <Button variant="contained" size="small" color="success" disabled startIcon={<CheckIcon />}>
        Saved
      </Button>
    );
  }
  return (
    <Button type="submit" variant="contained" size="small" {...props}>
      {children}
    </Button>
  );
}

export default function AccountPage() {
  const { state, authenticate } = useAuth();
  const message = useMessage();
  const user = state?.user || {};
  const memberships = user.client_memberships || [];
  const isClientMember = !user.is_staff && !user.is_superuser && memberships.length > 0;
  const primaryMembership = memberships[0] || {};

  const profileForm = useForm({ schema: profileSchema, defaultValues: { first_name: '', last_name: '' } });
  const emailForm = useForm({ schema: emailSchema, defaultValues: { email: '' } });
  const passwordForm = useForm({ schema: passwordSchema, defaultValues: { old_password: '', new_password1: '', new_password2: '' } });

  const [deactivateDialog, setDeactivateDialog] = useState(false);
  const [deactivateReason, setDeactivateReason] = useState('');
  const [deactivating, setDeactivating] = useState(false);
  const [deactivateSubmitted, setDeactivateSubmitted] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileInputRef = useRef(null);

  // Success states
  const [profileSaved, setProfileSaved] = useState(false);
  const [emailSaved, setEmailSaved] = useState(false);
  const [passwordSaved, setPasswordSaved] = useState(false);

  // Email change confirmation
  const [emailConfirmDialog, setEmailConfirmDialog] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');

  // Password strength — watch the form value directly
  const newPasswordValue = passwordForm.watch ? passwordForm.watch('new_password1') : '';
  const passwordStrength = useMemo(() => getPasswordStrength(newPasswordValue || ''), [newPasswordValue]);

  const userName = useMemo(() => {
    const full = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
    return full || 'Your account';
  }, [user.first_name, user.last_name]);

  const initials = useMemo(
    () => getInitials(user.first_name, user.last_name, user.email),
    [user.first_name, user.last_name, user.email]
  );

  const scopeName = useMemo(() => {
    if (primaryMembership.parent_company_name) return primaryMembership.parent_company_name;
    if (primaryMembership.client_name) return primaryMembership.client_name;
    return null;
  }, [primaryMembership]);

  const dashboardSlug = useMemo(() => {
    if (primaryMembership.client_slug) return primaryMembership.client_slug;
    if (primaryMembership.client_id) return primaryMembership.client_id;
    if (primaryMembership.parent_company_name) {
      return primaryMembership.parent_company_name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
    }
    return null;
  }, [primaryMembership]);

  useEffect(() => {
    profileForm.reset({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
    });
    emailForm.reset({ email: user.email || '' });
  }, [emailForm, profileForm, user.email, user.first_name, user.last_name]);

  const refreshUser = (data) => {
    const current = state || {};
    authenticate({ ...current, user: data });
  };

  const handleProfileSubmit = ({ first_name, last_name }) =>
    defaultClient
      .request({
        url: 'accounts/auth/user/',
        method: 'PATCH',
        payload: { first_name, last_name },
      })
      .then((resp) => {
        refreshUser(resp?.data || resp);
        message.success('Profile updated');
        setProfileSaved(true);
        setTimeout(() => setProfileSaved(false), 2000);
      })
      .catch(() => {
        message.error('Unable to update profile');
      });

  const handleEmailFormSubmit = ({ email }) => {
    if (email === user.email) {
      message.error('This is already your current email.');
      return;
    }
    setPendingEmail(email);
    setEmailConfirmDialog(true);
  };

  const handleEmailConfirm = () => {
    setEmailConfirmDialog(false);
    defaultClient
      .request({
        url: 'accounts/auth/user/',
        method: 'PATCH',
        payload: { email: pendingEmail },
      })
      .then((resp) => {
        refreshUser(resp?.data || resp);
        message.success('Email updated');
        setEmailSaved(true);
        setTimeout(() => setEmailSaved(false), 2000);
      })
      .catch(() => {
        message.error('Unable to update email');
      });
  };

  const handlePasswordSubmit = (payload) =>
    defaultClient
      .request({
        url: 'accounts/auth/password/change/',
        method: 'POST',
        payload,
      })
      .then(() => {
        message.success('Password updated');
        passwordForm.reset({ old_password: '', new_password1: '', new_password2: '' });
        setPasswordSaved(true);
        setTimeout(() => setPasswordSaved(false), 2000);
      })
      .catch((error) => {
        const detail = error?.response?.data?.detail || 'Unable to change password';
        message.error(detail);
      });

  const handleDeactivateRequest = useCallback(() => {
    setDeactivating(true);
    defaultClient
      .request({
        url: 'accounts/auth/team/request-deactivation/',
        method: 'POST',
        payload: { reason: deactivateReason.trim() },
      })
      .then(() => {
        setDeactivateSubmitted(true);
        message.success('Your deactivation request has been submitted.');
      })
      .catch((error) => {
        const detail = error?.response?.data?.detail;
        message.error(detail || 'Unable to submit request.');
      })
      .finally(() => setDeactivating(false));
  }, [deactivateReason, message]);

  const handleAvatarUpload = useCallback(
    (event) => {
      const file = event.target.files?.[0];
      if (!file) return;

      if (file.size > 5 * 1024 * 1024) {
        message.error('Image must be smaller than 5 MB.');
        return;
      }
      if (!file.type.startsWith('image/')) {
        message.error('File must be an image.');
        return;
      }

      setUploadingAvatar(true);
      const formData = new FormData();
      formData.append('image', file);

      defaultClient
        .request({
          url: 'accounts/auth/avatar/',
          method: 'POST',
          payload: formData,
          multipart: true,
        })
        .then((resp) => {
          const thumbUrl = resp?.data?.thumb_url;
          refreshUser({ ...user, thumb_url: thumbUrl });
          message.success('Profile photo updated');
        })
        .catch(() => {
          message.error('Unable to upload photo');
        })
        .finally(() => {
          setUploadingAvatar(false);
          if (fileInputRef.current) fileInputRef.current.value = '';
        });
    },
    [message, user, refreshUser]
  );

  const handleAvatarRemove = useCallback(() => {
    setUploadingAvatar(true);
    defaultClient
      .request({
        url: 'accounts/auth/avatar/',
        method: 'DELETE',
      })
      .then(() => {
        refreshUser({ ...user, thumb_url: null });
        message.success('Profile photo removed');
      })
      .catch(() => {
        message.error('Unable to remove photo');
      })
      .finally(() => setUploadingAvatar(false));
  }, [message, user, refreshUser]);

  const avatarSrc = user.thumb_url || undefined;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      {/* Account header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5} alignItems={{ sm: 'center' }}>
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={handleAvatarUpload}
              />
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                badgeContent={
                  <Tooltip title={avatarSrc ? 'Change photo' : 'Upload photo'}>
                    <IconButton
                      size="small"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingAvatar}
                      sx={{
                        bgcolor: 'background.paper',
                        border: '2px solid',
                        borderColor: 'divider',
                        width: 28,
                        height: 28,
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <CameraAltIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                }
              >
                <Avatar
                  src={avatarSrc}
                  sx={{
                    width: 72,
                    height: 72,
                    fontSize: 28,
                    fontWeight: 700,
                    bgcolor: avatarSrc ? undefined : 'primary.main',
                    cursor: 'pointer',
                  }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {!avatarSrc ? initials : null}
                </Avatar>
              </Badge>
              {avatarSrc && (
                <Tooltip title="Remove photo">
                  <IconButton
                    size="small"
                    onClick={handleAvatarRemove}
                    disabled={uploadingAvatar}
                    sx={{
                      position: 'absolute',
                      top: -4,
                      right: -4,
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      width: 20,
                      height: 20,
                      '&:hover': { bgcolor: 'error.light', color: 'error.contrastText' },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 12 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight={700}>
                {userName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user.email}
              </Typography>
              {isClientMember && (
                <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                  {scopeName && (
                    <Chip label={scopeName} size="small" variant="outlined" />
                  )}
                  <Chip
                    label={ROLE_LABELS[primaryMembership.role] || primaryMembership.role}
                    color={ROLE_COLORS[primaryMembership.role] || 'default'}
                    size="small"
                    variant="outlined"
                  />
                </Stack>
              )}
            </Box>
            <Stack spacing={0.5} alignItems={{ xs: 'flex-start', sm: 'flex-end' }}>
              <Typography variant="caption" color="text.secondary">
                Last login
              </Typography>
              <Typography variant="body2">
                {formatDate(user.last_login)}
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* LEFT COLUMN — Profile & Portal */}
        <Grid item xs={12} md={6}>
          <Stack spacing={3}>
            {/* Profile */}
            <Card>
              <CardHeader title="Profile" subheader="Update your name" />
              <Divider />
              <CardContent>
                <FormBase onSubmit={profileForm.handleSubmit({ onSubmit: handleProfileSubmit })}>
                  <Stack spacing={2}>
                    <TextField
                      control={profileForm.control}
                      fullWidth
                      label="First name"
                      name="first_name"
                      autoComplete="given-name"
                    />
                    <TextField
                      control={profileForm.control}
                      fullWidth
                      label="Last name"
                      name="last_name"
                      autoComplete="family-name"
                    />
                  </Stack>
                  <Box sx={{ mt: 2 }}>
                    <SaveButton saved={profileSaved}>Save profile</SaveButton>
                  </Box>
                </FormBase>
              </CardContent>
            </Card>

            {/* Email */}
            <Card>
              <CardHeader title="Email" subheader="Change the email you use to sign in" />
              <Divider />
              <CardContent>
                <FormBase onSubmit={emailForm.handleSubmit({ onSubmit: handleEmailFormSubmit })}>
                  <TextField control={emailForm.control} fullWidth label="Email" name="email" autoComplete="email" />
                  <Box sx={{ mt: 2 }}>
                    <SaveButton saved={emailSaved}>Update email</SaveButton>
                  </Box>
                </FormBase>
              </CardContent>
            </Card>

            {/* Portal access */}
            {isClientMember && (
              <Card>
                <CardHeader title="Portal access" subheader="Your current access level" />
                <Divider />
                <CardContent>
                  <Stack spacing={1.5}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Organization</Typography>
                      <Typography variant="body2" fontWeight={600}>{scopeName || '-'}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Role</Typography>
                      <Chip
                        label={ROLE_LABELS[primaryMembership.role] || primaryMembership.role}
                        color={ROLE_COLORS[primaryMembership.role] || 'default'}
                        size="small"
                        variant="outlined"
                      />
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Member since</Typography>
                      <Typography variant="body2">{formatShortDate(user.date_joined)}</Typography>
                    </Stack>
                    {memberships.length > 1 && (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">Memberships</Typography>
                        <Typography variant="body2">{memberships.length} clients</Typography>
                      </Stack>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            )}

            {/* Quick links */}
            <Card>
              <CardHeader title="Quick links" />
              <Divider />
              <CardContent>
                <Stack spacing={1}>
                  {isClientMember && dashboardSlug && (
                    <Button
                      component={Link}
                      href={paths.clientDashboard(dashboardSlug)}
                      startIcon={<DashboardIcon />}
                      sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                      color="inherit"
                    >
                      Performance Dashboard
                    </Button>
                  )}
                  {isClientMember && (
                    <Button
                      component={Link}
                      href={paths.teamManagement}
                      startIcon={<GroupIcon />}
                      sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                      color="inherit"
                    >
                      Team Management
                    </Button>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Grid>

        {/* RIGHT COLUMN — Security & Danger Zone */}
        <Grid item xs={12} md={6}>
          <Stack spacing={3}>
            {/* Password */}
            <Card>
              <CardHeader
                title="Password"
                subheader="Set a new password for your account"
                avatar={<SecurityIcon color="action" />}
              />
              <Divider />
              <CardContent>
                <FormBase onSubmit={passwordForm.handleSubmit({ onSubmit: handlePasswordSubmit })}>
                  <Stack spacing={2}>
                    <TextField
                      control={passwordForm.control}
                      fullWidth
                      label="Current password"
                      name="old_password"
                      type="password"
                      autoComplete="current-password"
                    />
                    <Box>
                      <TextField
                        control={passwordForm.control}
                        fullWidth
                        label="New password"
                        name="new_password1"
                        type="password"
                        autoComplete="new-password"
                      />
                      {newPasswordValue && (
                        <Box sx={{ mt: 1 }}>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                            <LinearProgress
                              variant="determinate"
                              value={passwordStrength.score}
                              color={passwordStrength.color}
                              sx={{ flex: 1, height: 6, borderRadius: 3 }}
                            />
                            <Typography variant="caption" color={`${passwordStrength.color}.main`} sx={{ minWidth: 70, textAlign: 'right' }}>
                              {passwordStrength.label}
                            </Typography>
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            Use 12+ characters with uppercase, lowercase, numbers, and symbols.
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <TextField
                      control={passwordForm.control}
                      fullWidth
                      label="Confirm new password"
                      name="new_password2"
                      type="password"
                      autoComplete="new-password"
                    />
                  </Stack>
                  <Box sx={{ mt: 2 }}>
                    <SaveButton saved={passwordSaved}>Update password</SaveButton>
                  </Box>
                </FormBase>
              </CardContent>
            </Card>

            {/* Password reset link */}
            <Card>
              <CardHeader
                title="Forgot your password?"
                subheader="Send yourself a reset link via email"
              />
              <Divider />
              <CardContent>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    if (!user.email) return;
                    defaultClient
                      .request({
                        auth: false,
                        url: 'accounts/auth/password/reset/',
                        method: 'POST',
                        payload: { email: user.email },
                      })
                      .then(() => message.success('Password reset link sent to your email'))
                      .catch(() => message.error('Unable to send reset link'));
                  }}
                >
                  Send reset link to {user.email}
                </Button>
              </CardContent>
            </Card>

            {/* Session info */}
            <Card>
              <CardHeader title="Session" subheader="Your login activity" />
              <Divider />
              <CardContent>
                <Stack spacing={1.5}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Last login</Typography>
                    <Typography variant="body2">{formatDate(user.last_login)}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Account created</Typography>
                    <Typography variant="body2">{formatShortDate(user.date_joined)}</Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            {/* Notification preferences */}
            <Card>
              <CardHeader title="Notifications" subheader="Email notification preferences" />
              <Divider />
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  Email notifications are currently managed by your Lead Gurus account team.
                </Typography>
              </CardContent>
            </Card>

            {/* Danger zone */}
            {isClientMember && (
              <Card sx={{ borderColor: 'error.main', borderWidth: 1, borderStyle: 'solid' }}>
                <CardHeader
                  title="Danger zone"
                  subheader="Irreversible actions"
                  titleTypographyProps={{ color: 'error' }}
                />
                <Divider />
                <CardContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Request account deactivation. This sends a request to the Lead Gurus team
                    who will remove your access. This action cannot be undone without a new invite.
                  </Typography>
                  {deactivateSubmitted ? (
                    <Chip label="Deactivation request submitted" color="warning" variant="outlined" />
                  ) : (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => setDeactivateDialog(true)}
                    >
                      Request account deactivation
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}
          </Stack>
        </Grid>
      </Grid>

      {/* Email change confirmation dialog */}
      <Dialog
        open={emailConfirmDialog}
        onClose={() => setEmailConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm email change</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to change your sign-in email from{' '}
            <strong>{user.email}</strong> to <strong>{pendingEmail}</strong>?
          </DialogContentText>
          <DialogContentText sx={{ mt: 1 }}>
            You will need to use the new email to log in going forward. If you enter an incorrect
            email you may lose access to your account.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailConfirmDialog(false)}>Cancel</Button>
          <Button onClick={handleEmailConfirm} variant="contained">
            Confirm change
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deactivation dialog */}
      <Dialog
        open={deactivateDialog}
        onClose={() => setDeactivateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Request account deactivation</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This will send a request to the Lead Gurus team to deactivate your account and remove
            your portal access. You will need a new invite to regain access.
          </DialogContentText>
          <MuiTextField
            label="Reason (optional)"
            value={deactivateReason}
            onChange={(e) => setDeactivateReason(e.target.value)}
            multiline
            rows={3}
            fullWidth
            placeholder="e.g. I'm leaving the company, no longer need access..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateDialog(false)} disabled={deactivating}>
            Cancel
          </Button>
          <Button
            onClick={handleDeactivateRequest}
            color="error"
            variant="contained"
            disabled={deactivating}
          >
            {deactivating ? 'Submitting...' : 'Submit request'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

AccountPage.displayName = 'AccountPage';
